import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Constant} from '../../../global/util/Constant';
import {LocalStorage} from '../../../global/util/LocalStorage';
import {Person} from '../../model/Person';
import {AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {ProfileService} from '../../service/profile.service';
import {Router} from '@angular/router';
import HttpStatusCode from '../../../global/util/HttpStatusCode';
import {InformType} from '../../../global/util/enum/InformType';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ApplicationService} from '../../service/application.service';
import {AuthService} from '../../service/auth/auth.service';
import {Dialog} from '../../../global/util/Dialog';
import {StringUtil} from '../../../global/util/StringUtil';
import {TranslateService} from '@ngx-translate/core';
import {PasswordLevel} from '../../../global/util/PasswordLevel';
import {Passwordsecuritylevel} from '../../model/Passwordsecuritylevel';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {
  public person = new Person();
  public title = 'TITLE.USER-PROFILE';
  public personForm: FormGroup;
  public loading: boolean;
  public areSamePasswords = true;
  public minLengthPassword = 12;
  public maxLengthPassword = 16;
  public disablePassword = true;
  public readonly DEFAULT_PASSWORD = '';
  public readonly DEFAULT_PASSWORD_EMPTY = '';
  public ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  public REQUIRED_CAPTCHA = false;
  public rubricSrc: string | ArrayBuffer;
  public previewRubricWidth = '355';
  public previewRubricHeight = '150';
  public viewActualPassword: boolean;
  public viewNewPassword: boolean;
  public viewNewPasswordRepeated: boolean;
  public showPasswordButton: boolean;
  @ViewChild('rubricPic', {static: false}) rubricPic: ElementRef;
  public readonly OLD_PASSWORD = 1;
  public readonly NEW_PASSWORD = 2;
  public readonly NEW_REPEAT_PASSWORD = 3;
  // private passwordValidators = [Validators.required,
  //     PasswordValidator.requiredLowercase(),
  //     PasswordValidator.requiredUppercase(),
  //     PasswordValidator.minLength(this.minLengthPassword),
  //     PasswordValidator.maxLength(this.maxLengthPassword)
  // ];
  passwordSecurityLevel = new Passwordsecuritylevel();
  isSecurityHigh = false;
  isSecurityMedium = true;
  isSecurityLow = false;

  constructor(private profileService: ProfileService,
              private formBuilder: FormBuilder,
              private router: Router,
              private modalService: NgbModal,
              private applicationService: ApplicationService,
              private authenticationService: AuthService,
              public translate: TranslateService) {
    Dialog.createInstance(modalService);
    this.createForm();
    this.loadDefaultData();
  }

  ngOnInit(): void {
    if (this.disablePassword) {
      this.personForm.get('newPassword').clearValidators();
      this.personForm.get('repeatNewPassword').clearValidators();
      this.updateValuesAndValidities();
    }
  }

  private loadDefaultData() {
    this.loading = true;
    const personId = LocalStorage.getPersonId();
    const url = Constant.ROOT_API_V1.concat('/people/', String(personId));
    this.profileService.get(url).subscribe((value: Person) => {
      this.person = value;
      this.passwordSecurityLevel = value._more.passwordSecurityLevel ? value._more.passwordSecurityLevel : null;
      this.setDefaultValues();
      this.loading = false;
    }, error => {
      this.loading = false;
    });
  }

  private setDefaultValues() {
    this.person.password = this.DEFAULT_PASSWORD;
    this.person.repeatNewPassword = this.DEFAULT_PASSWORD;
    this.loadRubricSrc(this.person);
    this.personForm.get('rubricFilename2Show').setValue(this.person._more.rubricFilename);
  }

  public createForm() {
    // oldPassword: ['', Validators.compose(this.passwordValidators)],
    this.personForm = this.formBuilder.group({
      oldPassword: [''],
      newPassword: ['', [Validators.required, Validators.minLength(12), this.passwordComplexityMedium, this.noWhitespaceValidator()]],
      repeatNewPassword: ['', [Validators.required, Validators.minLength(12), this.noWhitespaceValidator()]],
      email: ['', Validators.compose([Validators.required, Validators.email])],
      cellphone: [''],
      recaptchaReactive: [''],
      rubricFileBase64: [''],
      rubricFilename: [''],
      rubricFilename2Show: [''],
    }, {validator: this.passwordMatchValidator});
  }

  public save() {
    if (this.personForm.invalid || !this.areSamePasswords) {
      this.markAsTouchedAllControls();
      return;
    }
    // if (!this.disablePassword) {
    const newPasswordControl = this.person.password;
    const confirmPasswordControl = this.person.repeatNewPassword;
    const passwordRequirements =
      this.isSecurityHigh ? (this.passwordControls.isValidLength &&
          this.passwordControls.hasUpperCase &&
          this.passwordControls.hasLowerCase &&
          this.passwordControls.hasNumber &&
          this.passwordControls.hasNoSpaces &&
          this.passwordControls.hasSpecialChar) :
        (this.isSecurityMedium ? (this.passwordControls.isValidLength &&
            this.passwordControls.hasUpperCase &&
            this.passwordControls.hasLowerCase &&
            this.passwordControls.hasNumber &&
            this.passwordControls.hasNoSpaces) :
          this.passwordControls.isValidLength && this.passwordControls.hasNoSpaces);
    if (!passwordRequirements && !this.disablePassword) {
      this.personForm.markAllAsTouched();
      this.handleFormErrors(newPasswordControl, confirmPasswordControl, this.passwordControls.hasNoSpaces);
      return;
    }
    this.setValuesBefore2Save();
    this.loading = true;
    const personId = LocalStorage.getPersonId();
    const url = Constant.ROOT_API_V1.concat('/people/', String(personId), '/profile');
    this.profileService.update(url, this.person).subscribe(value => {
      this.loading = false;
      if (!this.hasRole(this.ROLE_SUPERADMIN)) {
        this.router.navigate(['document']);
      } else {
        this.router.navigate(['enterprise']);
      }
    }, error => {
      this.loading = false;
      this.processError(error);
    });
  }

  private showDanger(messageKey: string) {
    Dialog.show(InformType.Danger, this.translate.instant('TITLE.CHANGE-PASSWORD'), this.translate.instant(messageKey));
  }

  private handleFormErrors(newPasswordControl: any, confirmPasswordControl: any, hasNoSpaces?: any) {
    this.personForm.markAllAsTouched();
    if (newPasswordControl.invalid) {
      this.showDanger('MESSAGE.PASSWORD-REQUIRED-MUST-MEET-REQUIREMENTS');
    } else if (newPasswordControl.value !== confirmPasswordControl.value) {
      this.showDanger('MESSAGE.PASSWORDS-DO-NO-MATCH');
    } else {
      this.showDanger('MESSAGE.PASSWORD-REQUIRED-MUST-MEET-REQUIREMENTS');
    }
  }

  private setValuesBefore2Save() {
    if (this.DEFAULT_PASSWORD === this.person.password || this.DEFAULT_PASSWORD === this.person.repeatNewPassword) {
      this.person.newPassword = undefined;
    } else if (this.areSamePasswords) {
      this.person.newPassword = this.person.password;
    }
    this.person.rubricBase64Data = this.personForm.get('rubricFileBase64').value;
    this.person.rubricFilename = this.personForm.get('rubricFilename').value;
  }

  private markAsTouchedAllControls() {
    Object.values(this.personForm.controls).forEach(control => {
      control.markAsDirty();
    });
  }

  public cancel() {
    if (!this.hasRole(this.ROLE_SUPERADMIN)) {
      this.router.navigate(['document']);
    } else {
      this.router.navigate(['enterprise']);
    }
  }

  public isInvalidControl(name: string) {
    return this.personForm.get(name).invalid && this.personForm.get(name).dirty;
  }

  public getErrorMessageBy(ctrl: string): string {
    const control = this.personForm.get(ctrl);
    const required = control.hasError('required');
    const requiredLowercase = control.hasError('requiredLowercase');
    const requiredUppercase = control.hasError('requiredUppercase');
    const minLength = control.hasError('minLength');
    const maxLength = control.hasError('maxLength');
    const whitespace = control.hasError('whitespace');
    switch (ctrl) {
      case 'oldPassword':
      case 'newPassword':
      case 'repeatNewPassword':
        if (required) {
          return 'MESSAGE.PASSWORD-IS-REQUIRED';
        } else if (requiredLowercase) {
          return 'MESSAGE.PASSWORD-SHOULD-CONTAIN-LOWERCASE-LETTER';
        } else if (requiredUppercase) {
          return 'MESSAGE.PASSWORD-SHOULD-CONTAIN-UPPERCASE-LETTER';
        } else if (minLength) {
          return String(this.translate.instant('MESSAGE.PASSWORD-SHOULD-CONTAIN-AT-LEAST-CHARACTERS')), String(control.errors.minLengthValue);
        } else if (maxLength) {
          return String(this.translate.instant('MESSAGE.PASSWORD-SHOULD-CONTAIN-AT-MOST-CHARACTERS')), String(control.errors.maxLengthValue);
        } else if (whitespace) {
          return String(this.translate.instant('MESSAGE.PASSWORD-DOES-NOT-CONTAIN-SPACE'));
        }
        break;
      case 'email':
        const hasPatternError = control.hasError('pattern');
        if (required) {
          return 'MESSAGE.EMAIL-IS-REQUIRED';
        } else if (hasPatternError) {
          const actualValue = control.errors.pattern.actualValue;
          return actualValue.concat(this.translate.instant('MESSAGE.NOT-COMPLY-WITH-REQUIRED-FORMAT'));
        }
        break;
    }
  }

  public matchPasswords(event, otherControl: string) {
    const value: string = event.target.value;
    const otherValue: string = this.personForm.get(otherControl).value;
    if (value === otherValue) {
      this.areSamePasswords = true;
    } else {
      this.areSamePasswords = false;
    }
    this.removeSpaces(otherControl);
  }

  public disablePasswordControls(event) {
    this.disablePassword = !this.disablePassword;
    if (event.target.checked) {
      this.person.password = this.DEFAULT_PASSWORD_EMPTY;
      this.person.repeatNewPassword = this.DEFAULT_PASSWORD_EMPTY;
      this.personForm.get('newPassword').setValidators([Validators.required, this.passwordComplexityMedium]);
      this.personForm.get('repeatNewPassword').setValidators(Validators.required);
      this.updateValuesAndValidities();
    } else {
      this.person.password = this.DEFAULT_PASSWORD;
      this.person.repeatNewPassword = this.DEFAULT_PASSWORD;
      this.personForm.get('newPassword').clearValidators();
      this.personForm.get('repeatNewPassword').clearValidators();
      this.updateValuesAndValidities();
      this.areSamePasswords = true;
    }
  }

  private updateValuesAndValidities() {
    this.personForm.get('newPassword').updateValueAndValidity();
    this.personForm.get('repeatNewPassword').updateValueAndValidity();
  }

  private processError(error: any) {
    const status = error.status;
    this.title = this.translate.instant('TITLE.USER-PROFILE');
    if (status) {
      if (HttpStatusCode.UNAUTHORIZED === status) {
        Dialog.show(InformType.Danger, this.title, error.error.message);
        this.person.oldPassword = '';
      } else if (HttpStatusCode.PAYMENT_REQUIRED === status) {
        this.applyCaptchaValidators();
        this.REQUIRED_CAPTCHA = true;
        this.person.oldPassword = '';
        this.personForm.get('recaptchaReactive').setValue('');
        Dialog.show(InformType.Danger, this.title, error.error.message);
      } else if (HttpStatusCode.FORBIDDEN === status) {
        Dialog.show(InformType.Info, this.translate.instant('TITLE.SESSION-EXPIRED'), this.translate.instant('MESSAGE.SESSION-EXPIRED'));
        this.logout();
      } else {
        Dialog.show(InformType.Danger, this.title);
      }
    } else {
      Dialog.show(InformType.Danger, this.title, error.error.message);
    }
  }

  private applyCaptchaValidators() {
    this.personForm.get('recaptchaReactive').setValidators(Validators.required);
    this.personForm.get('recaptchaReactive').updateValueAndValidity();
  }

  private logout() {
    this.applicationService.setUserLoggedIn(false);
    this.router.navigate(['login']);
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  public readSelectedRubric(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = e => this.rubricSrc = reader.result;
      reader.readAsDataURL(file);
    }
  }

  public changeStatus2(event: any) {
    const testFile = event[Constant.ZERO_INDEX];
    this.personForm.patchValue({
      rubricFileBase64: testFile.base64,
      rubricFilename: testFile.name,
      rubricFilename2Show: StringUtil.cut(testFile.name, 42, this.translate.instant('MESSAGE.SELECT')),
    });
    this.updateValueAndValidityRubricFields();
  }

  private updateValueAndValidityRubricFields() {
    this.personForm.get('rubricFileBase64').updateValueAndValidity();
    this.personForm.get('rubricFilename').updateValueAndValidity();
    this.personForm.get('rubricFilename2Show').updateValueAndValidity();
  }

  get defaultRubricSrc(): string | ArrayBuffer {
    return this.rubricSrc || this.placeHoldItRubric;
  }

  get placeHoldItRubric(): string {
    return Constant.DEFAULT_IMAGE_PATH_355_150;
  }

  get styleDimensionsRubric(): string {
    return 'width: ' + this.previewRubricWidth + 'px; height: auto';
  }

  private loadRubricSrc(person: Person) {
    if (person && person._more.rubricFileExtension && person._more.base64RubricFile) {
      this.rubricSrc = 'data:image/' + person._more.rubricFileExtension + ';base64,' + person._more.base64RubricFile;
    } else {
      this.rubricSrc = this.placeHoldItRubric;
    }
  }

  public toggleFieldTextType(passwordType: number) {
    if (this.OLD_PASSWORD === passwordType) {
      this.viewActualPassword = !this.viewActualPassword;
    } else if (this.NEW_PASSWORD === passwordType) {
      this.viewNewPassword = !this.viewNewPassword;
    } else if (this.NEW_REPEAT_PASSWORD === passwordType) {
      this.viewNewPasswordRepeated = !this.viewNewPasswordRepeated;
    }
  }

  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPassword = group.get('newPassword').value;
    const confirmPassword = group.get('repeatNewPassword').value;
    return newPassword === confirmPassword ? null : {mismatch: true};
  }

  passwordComplexityMedium(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    if (!password) {
      return null;
    }
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = specialCharRegex.test(password);
    const asNoSpaces = !/\s/.test(password);
    const valid =  asNoSpaces;
    return valid ? null : {passwordComplexity: true};
  }

  get passwordControls() {
    const level = this.passwordSecurityLevel !== null ? this.passwordSecurityLevel.level : 3;
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;
    switch (level) {
      case PasswordLevel.PASSWORD_HIGH:
        this.isSecurityHigh = true;
        this.isSecurityMedium = false;
        this.isSecurityLow = false;
        return {
          hasUpperCase: /[A-Z]/.test(this.personForm.get('newPassword').value),
          hasLowerCase: /[a-z]/.test(this.personForm.get('newPassword').value),
          hasNumber: /[0-9]/.test(this.personForm.get('newPassword').value),
          hasSpecialChar: specialCharRegex.test(this.personForm.get('newPassword').value),
          hasNoSpaces: !/\s/.test(this.personForm.get('newPassword').value),
          isValidLength: this.personForm.get('newPassword').value?.length > 15
        };
      case PasswordLevel.PASSWORD_MEDIUM:
        this.isSecurityHigh = false;
        this.isSecurityMedium = true;
        this.isSecurityLow = false;
        return {
          hasUpperCase: /[A-Z]/.test(this.personForm.get('newPassword').value),
          hasLowerCase: /[a-z]/.test(this.personForm.get('newPassword').value),
          hasNumber: /[0-9]/.test(this.personForm.get('newPassword').value),
          hasNoSpaces: !/\s/.test(this.personForm.get('newPassword').value),
          isValidLength: this.personForm.get('newPassword').value?.length > 11
        };
      case PasswordLevel.PASSWORD_LOW:
        this.isSecurityHigh = false;
        this.isSecurityMedium = false;
        this.isSecurityLow = true;
        return {
          hasNoSpaces: !/\s/.test(this.personForm.get('newPassword').value),
          isValidLength: this.personForm.get('newPassword').value?.length > 7
        };
    }
    this.personForm.get('newPassword').updateValueAndValidity();
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isWhitespace = (control.value || '').trim().length !== control.value.length;
      const hasInnerSpaces = (control.value || '').includes(' ');
      const isValid = !isWhitespace && !hasInnerSpaces;
      return isValid ? null : {whitespace: true};
    };
  }

  removeSpaces(controlName: string): void {
    const control = this.personForm.get(controlName);
    if (control && control.value) {
      control.setValue(control.value.replace(/\s/g, '')); // Elimina todos los espacios en blanco
    }
  }

  validatePassword(): void {
    const newPasswordControl = this.personForm.get('newPassword');
    const repeatNewPasswordControl = this.personForm.get('repeatNewPassword');
    if (newPasswordControl && newPasswordControl.value) {
      newPasswordControl.setValue(newPasswordControl.value.replace(/\s/g, ''));
    }
    if (repeatNewPasswordControl && repeatNewPasswordControl.value) {
      repeatNewPasswordControl.setValue(repeatNewPasswordControl.value.replace(/\s/g, ''));
    }
    if (newPasswordControl.value !== repeatNewPasswordControl.value) {
      this.areSamePasswords = false;
    } else {
      this.areSamePasswords = true;
      repeatNewPasswordControl.setErrors(null); // Limpiar el error si coinciden
    }
  }
}
